import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import henImg from '../../assets/images/hen.png';
import deadImg from '../../assets/images/chicken_dead.png';
import FIRE_FRAMES from '../../utils/fireFrames';
import bgmSrc from '../../assets/sounds/Soundtrack.webm';
import '../../styles/chicken.css';
import { startChickenApi, stepChickenApi, cashoutChickenApi, getChickenActiveApi, getChickenHistoryApi } from '../../api/chickenApi';
import { normalizeRound, normalizeBet } from '../../utils/chicken';
import { walletTotal, dtShort } from '../../utils/format';

// Start balance wallet (localStorage/profile) se aata hai; wallet na ho to demo balance
const START_BALANCE = 1000000;

// Autoplay: round khatam hone ke baad agle round se pehle ruk-ne ka time (ms)
const AUTO_RESET = 1300; // board reset
const AUTO_GAP = AUTO_RESET + 300; // agla round start
const SPIN_PRESETS = [25, 50, 75, 100];
const HIST_PAGE = 10;

const fmt = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const cap = (k) => k.charAt(0) + k.slice(1).toLowerCase();
const AVATARS = ['🐔', '🐓', '🐥', '🦆', '🦉'];
const LIVE_NAMES = ['Teal Thick Hor...', 'Tomato Unive...', 'Jade Legitiman...', 'Silver Bizar...', 'Amber Quiet Fox'];

// Win confetti (sirf decoration)
const CONFETTI_COLORS = ['#ffd23f', '#3ddc6a', '#ff6b5e', '#4cc9f0', '#f72585', '#ffffff'];
const CONFETTI = Array.from({ length: 28 }, (_, i) => ({
  left: (i * 37) % 100,
  delay: ((i * 53) % 60) / 100,
  dur: 1.4 + ((i * 17) % 10) / 10,
  color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
  rot: (i * 47) % 360,
}));

const RULES = (
  <>
    <p>Pick a bet and difficulty, press Play, then press GO to move the chicken across the road one lane at a time.</p>
    <p>Each lane raises your multiplier. Press CASH OUT any time to collect bet × multiplier.</p>
    <p>If fire bursts from the sewer grate, the chicken is burnt and the bet is lost. Higher difficulty means a higher chance of collision and bigger multipliers.</p>
  </>
);

const Icon = ({ d }) => (
  <span className="ic">
    <svg viewBox="0 0 24 24">
      <path d={d} />
    </svg>
  </span>
);

// Paar ho chuki lane ka gold coin (roast chicken wala) - SVG, koi image file nahi chahiye
const CoinSvg = () => (
  <svg className="coin-svg" viewBox="0 0 64 64" aria-hidden="true">
    <defs>
      <radialGradient id="cgold" cx="35%" cy="28%" r="85%">
        <stop offset="0" stopColor="#fff0a0" />
        <stop offset="0.5" stopColor="#f7be1a" />
        <stop offset="1" stopColor="#c47d00" />
      </radialGradient>
      <linearGradient id="cmeat" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor="#e39a3b" />
        <stop offset="1" stopColor="#a8571a" />
      </linearGradient>
    </defs>
    <circle cx="32" cy="32" r="30" fill="url(#cgold)" stroke="#b46f00" strokeWidth="2" />
    <circle cx="32" cy="32" r="24.5" fill="none" stroke="rgba(120,70,0,.35)" strokeWidth="2" />
    <circle cx="32" cy="32" r="27" fill="none" stroke="rgba(255,255,255,.35)" strokeWidth="1" />
    {/* roast chicken */}
    <ellipse cx="32" cy="35" rx="15" ry="11" fill="url(#cmeat)" stroke="#8a4510" strokeWidth="1.2" />
    <ellipse cx="27" cy="31" rx="7" ry="3.4" fill="rgba(255,225,160,.55)" />
    <ellipse cx="21" cy="42" rx="4.2" ry="3" fill="#c0701f" stroke="#8a4510" strokeWidth="1" />
    <ellipse cx="43" cy="42" rx="4.2" ry="3" fill="#c0701f" stroke="#8a4510" strokeWidth="1" />
    <circle cx="16" cy="45" r="2" fill="#fff6e0" stroke="#b9a37a" strokeWidth=".8" />
    <circle cx="48" cy="45" r="2" fill="#fff6e0" stroke="#b9a37a" strokeWidth=".8" />
    <path d="M27 33l3 3M30 33l-3 3M35 33l3 3M38 33l-3 3" stroke="#5a2a08" strokeWidth="1.2" strokeLinecap="round" />
    <circle cx="25" cy="25" r="2.2" fill="#fff6e0" stroke="#b9a37a" strokeWidth=".8" />
    <circle cx="39" cy="25" r="2.2" fill="#fff6e0" stroke="#b9a37a" strokeWidth=".8" />
  </svg>
);

/**
 * config = GET /chicken/config ka `data`
 * { isActive, minBet, maxBet, difficulties: { EASY: { steps, collisionChance, multipliers[] }, ... } }
 */

// Crash par aag: fire_1..fire_21 frames ek baar chalte hain (bhadakti -> bhabhakti -> chingaariyan)
const FIRE_FPS = 28;
let fireLoaded = false;
function preloadFire() {
  if (fireLoaded) return;
  fireLoaded = true;
  [deadImg, ...FIRE_FRAMES].forEach((src) => {
    const im = new Image();
    im.src = src;
  });
}

function FireSprite() {
  const [f, setF] = useState(0);
  useEffect(() => {
    preloadFire();
    let i = 0;
    const t = setInterval(() => {
      i += 1;
      if (i >= FIRE_FRAMES.length) {
        clearInterval(t);
        return;
      }
      setF(i);
    }, 1000 / FIRE_FPS);
    return () => clearInterval(t);
  }, []);
  return <img className="fire-frame" src={FIRE_FRAMES[f]} alt="" draggable={false} />;
}

// Crash se pehle grate se nikalti chhoti chingaari: fire_1..fire_3 (chhoti lapat) badhti aur ghatti hai
const EMBER_SEQ = [0, 1, 2, 2, 1, 2, 1, 0];
function EmberSprite() {
  const [k, setK] = useState(0);
  const [done, setDone] = useState(false);
  useEffect(() => {
    preloadFire();
    let i = 0;
    const t = setInterval(() => {
      i += 1;
      if (i >= EMBER_SEQ.length) {
        clearInterval(t);
        setDone(true);
        return;
      }
      setK(i);
    }, 100);
    return () => clearInterval(t);
  }, []);
  if (done) return null;
  return <img className="ember-frame" src={FIRE_FRAMES[EMBER_SEQ[k]]} alt="" draggable={false} />;
}

export default function ChickenRoadGame({ config, activeRound = null }) {
  const { isActive, minBet, maxBet, difficulties } = config;
  const diffKeys = Object.keys(difficulties);
  const navigate = useNavigate();
  const { user, wallet, logout, refreshProfile } = useAuth();

  // Game ki saari mutable state ek object (G) me hai, badalne ke baad render() chalta hai
  const [, setTick] = useState(0);
  const render = useCallback(() => setTick((n) => n + 1), []);
  const ref = useRef(null);
  if (!ref.current) {
    const firstBet = Math.min(maxBet, Math.max(minBet, 50));
    // GET /chicken/active se adhura round mila to wahi se resume
    const ar = normalizeRound(activeRound);
    const resume = ar && ar.status === 'ACTIVE' && difficulties[ar.difficulty] ? ar : null;
    ref.current = {
      bal: wallet ? walletTotal(wallet) : START_BALANCE,
      bet: resume ? resume.betAmount : firstBet,
      betText: String(resume ? resume.betAmount : firstBet),
      diff: resume ? resume.difficulty : diffKeys[0],
      pos: resume ? resume.currentStep : 0,
      st: resume ? 'playing' : 'idle', // idle | playing | over
      busy: false,
      roundId: resume ? resume.gameId : null,
      histRows: null, // /chicken/history (modal ke liye)
      histErr: '',
      opt: { snd: true, mus: true, spc: true },
      started: Boolean(resume),
      hopKey: 0,
      hop: false,
      burn: false,
      char: false,
      comeback: false, // win ke baad chicken wapis aane ki animation
      embers: [], // lane index jinke grate se chingaari/aag nikal rahi hai
      embKey: 0,
      eggKey: 0, // board reset par egg phir se upar se girta hai
      embT: null,
      fireAt: 0,
      pop: null,
      popShow: false,
      menu: false,
      modal: null, // 'rules' | 'fair' | 'hist' | 'how'
      histShown: HIST_PAGE, // history modal me abhi kitni rows dikh rahi hain (Load more)
      amod: false, // autoplay modal open?
      acfg: { spins: 10, target: 2 }, // autoplay settings
      auto: { on: false, left: 0 }, // autoplay chal raha hai? kitne spins bache
      note: '',
      flash: false,
      avatar: 0,
      live: { online: 1277, name: '405694', amt: '+₹2,330.00' },
      ac: null,
      pt: null,
      rt: null,
      cb: null,
      timers: [],
    };
  }
  const G = ref.current;

  const bgmRef = useRef(null);
  const menuRef = useRef(null);
  const playRef = useRef(null);
  const scrollRef = useRef(null); // scene ka horizontal scroller (hand-tool drag + auto follow)
  const dragRef = useRef(null);
  const followKey = useRef('');

  const mults = () => difficulties[G.diff].multipliers;
  const stepsOf = () => difficulties[G.diff].steps || mults().length;

  const later = (fn, ms) => {
    const id = setTimeout(fn, ms);
    G.timers.push(id);
    return id;
  };

  const beep = (f, d = 0.12, t = 'sine') => {
    if (!G.opt.snd) return;
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      G.ac = G.ac || new AC();
      const o = G.ac.createOscillator();
      const g = G.ac.createGain();
      o.type = t;
      o.frequency.value = f;
      g.gain.value = 0.05;
      o.connect(g);
      g.connect(G.ac.destination);
      o.start();
      g.gain.exponentialRampToValueAtTime(0.001, G.ac.currentTime + d);
      o.stop(G.ac.currentTime + d);
    } catch (e) {
      /* audio optional */
    }
  };

  const board = () => {
    clearTimeout(G.pt);
    clearTimeout(G.rt);
    G.popShow = false;
    G.pos = 0;
    G.busy = false;
    G.fireAt = 0;
    G.hop = false;
    G.burn = false;
    G.char = false;
    G.embers = [];
    G.eggKey += 1;
    render();
  };

  // chalte waqt aage wali lanes ke grate se aag nikalna
  const setEmbers = (list, ms = 800) => {
    G.embers = list;
    G.embKey += 1;
    clearTimeout(G.embT);
    G.embT = setTimeout(() => {
      G.embers = [];
      render();
    }, ms);
    render();
  };

  const popup = (win, mult, amt) => {
    G.pop = { win, mult, amt };
    G.popShow = true;
    clearTimeout(G.pt);
    G.pt = later(() => {
      G.popShow = false;
      render();
    }, G.auto.on ? 1100 : 3200);
    render();
  };

  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  const errMsg = (e) => e.response?.data?.message || e.message || 'Something went wrong';
  const flashErr = () => {
    G.flash = true;
    render();
    later(() => {
      G.flash = false;
      render();
    }, 400);
  };

  // server ka wallet (GET /user/profile) -> header ka balance + localStorage wallet
  const syncWallet = async () => {
    try {
      const data = await refreshProfile();
      if (data?.wallet) {
        G.bal = walletTotal(data.wallet);
        render();
      }
    } catch (e) {
      /* balance agli baar sync ho jayega */
    }
  };

  // server wala round board par laga do
  const applyRound = (r) => {
    board();
    G.roundId = r.gameId;
    G.diff = r.difficulty;
    G.bet = r.betAmount;
    G.betText = String(r.betAmount);
    G.pos = r.currentStep;
    G.st = 'playing';
    G.started = true;
    render();
  };

  // server se phir mila lo: round chal raha ho to resume, warna idle
  const resync = async () => {
    try {
      const a = await getChickenActiveApi();
      const r = normalizeRound(a.data?.data);
      if (r && r.status === 'ACTIVE' && difficulties[r.difficulty]) applyRound(r);
      else {
        G.st = 'idle';
        G.roundId = null;
        board();
      }
    } catch (e) {
      /* ignore */
    }
  };

  const crash = () => {
    clearTimeout(G.embT);
    G.embers = [];
    G.st = 'over';
    G.fireAt = G.pos;
    G.burn = true;
    G.roundId = null;
    beep(120, 0.6, 'sawtooth');
    const fast = G.auto.on;
    later(() => {
      G.char = true;
      render();
    }, fast ? 300 : 500);
    later(() => {
      G.busy = false;
      popup(false, 0, G.bet);
    }, fast ? 500 : 1100);
    G.rt = later(() => {
      G.st = 'idle';
      board();
    }, fast ? AUTO_RESET : 3300);
    render();
    syncWallet();
  };

  // win: manual cashout ya last lane par auto cashout
  const finishWin = (d) => {
    const mu = Number(d.cashoutMultiplier ?? d.currentMultiplier) || (G.pos ? mults()[G.pos - 1] : 1);
    const w = Number(d.winAmount) || +(G.bet * mu).toFixed(2);
    G.st = 'over';
    G.busy = false;
    G.roundId = null;
    beep(880, 0.25);
    later(() => beep(1175, 0.35), 180); // chhota "ding-ding" win sound
    popup(true, mu, w);

    // popup dikhne ke baad chicken wapis door ke paas (start) aa jaye
    clearTimeout(G.rt);
    G.rt = later(() => {
      G.st = 'idle';
      board();
      G.comeback = true; // wapis aane ki animation
      render();
      clearTimeout(G.cb);
      G.cb = later(() => {
        G.comeback = false;
        render();
      }, 900);
    }, G.auto.on ? AUTO_RESET : 3400);

    render();
    syncWallet();
  };

  // POST /chicken/cashout -> true agar cashout ho gaya
  const doCash = async () => {
    if (G.busy || G.st !== 'playing' || !G.pos || !G.roundId) return false;
    G.busy = true;
    render();
    try {
      const res = await cashoutChickenApi({ gameId: G.roundId });
      const body = res.data;
      if (!body?.success) throw new Error(body?.message || 'Cashout failed');
      finishWin(body.data || {});
      return true;
    } catch (e) {
      console.error('cashout error:', errMsg(e));
      G.busy = false;
      flashErr();
      if (e.response) resync();
      return false;
    }
  };
  const cash = () => {
    doCash();
  };

  // POST /chicken/step (server batata hai chicken bacha ya jal gaya)
  // return: 'safe' | 'dead' | 'won' (last lane auto cashout) | 'error' | 'skip'
  const doStep = async () => {
    if (G.busy || G.st !== 'playing' || !G.roundId || G.pos >= stepsOf()) return 'skip';
    const from = G.pos;
    G.busy = true;
    G.pos = from + 1;
    G.hop = true;
    G.hopKey += 1;
    beep(520 + G.pos * 30);
    // chicken chalte hi aage wale grates se aag nikle
    setEmbers([G.pos, G.pos + 1, G.pos + 2].filter((i) => i < mults().length), 900);
    render();
    try {
      const [res] = await Promise.all([stepChickenApi({ gameId: G.roundId, expectedStep: from }), wait(480)]);
      const body = res.data;
      if (!body?.success) throw new Error(body?.message || 'Step failed');
      const d = body.data || {};
      const step = Number(d.currentStep ?? d.step);

      const lost = d.result !== 'SAFE' || (d.diedAtStep !== null && d.diedAtStep !== undefined);
      if (lost) {
        G.pos = Number(d.diedAtStep ?? d.step) || G.pos;
        crash();
        return 'dead';
      }
      if (step) G.pos = step;
      if (d.autoCashout === true || (d.status && d.status !== 'ACTIVE')) {
        finishWin(d);
        return 'won';
      }

      G.busy = false;
      render();
      return 'safe';
    } catch (e) {
      console.error('step error:', errMsg(e));
      G.pos = from;
      G.hop = false;
      G.busy = false;
      flashErr();
      if (e.response) resync();
      return 'error';
    }
  };
  const go = () => {
    doStep();
  };

  // POST /chicken/start -> 'started' | 'resumed' | 'error'
  const doStart = async () => {
    if (G.busy || !isActive) return 'error';
    if (!(G.bet >= minBet) || G.bet > maxBet || G.bet > G.bal) {
      flashErr();
      return 'error';
    }

    G.busy = true; // double click rokne ke liye
    render();
    try {
      const res = await startChickenApi({ amount: G.bet, difficulty: G.diff });
      const body = res.data;
      if (!body?.success) throw new Error(body?.message || 'Round start nahi hua');
      const d = body.data || {};

      board(); // purani state reset (busy bhi false ho jata hai)
      G.comeback = false;
      G.roundId = d.gameId;
      G.bal -= G.bet; // turant dikhane ke liye, neeche server se sync hota hai
      G.st = 'playing';
      G.started = true;
      if (G.opt.mus) playBgm();
      beep(440);
      render();
      syncWallet();
      return 'started';
    } catch (e) {
      const msg = errMsg(e);
      console.error('start error:', msg);
      // server par purana round khula hai -> wahi resume karo
      if (/already have an active/i.test(msg)) {
        try {
          const a = await getChickenActiveApi();
          let r = normalizeRound(a.data?.data);
          if (!r) {
            const h = await getChickenHistoryApi(); // active na de to history me ACTIVE row dhundo
            r = normalizeRound(h.data?.data);
          }
          if (r && r.status === 'ACTIVE' && difficulties[r.difficulty]) {
            applyRound(r);
            return 'resumed';
          }
        } catch (err) {
          console.error('resume error:', errMsg(err));
        }
      }
      G.busy = false;
      flashErr();
      return 'error';
    }
  };

  const play = async () => {
    if (G.busy || G.auto.on) return;
    if (G.st === 'playing') return go();
    await doStart();
  };

  // ---------- AUTOPLAY ----------
  // Settings: kitne spins + kis step par cashout. Har spin = start -> N step -> cashout (ya burn)
  const showNote = (msg) => {
    G.note = msg;
    render();
    clearTimeout(G.noteT);
    G.noteT = setTimeout(() => {
      G.note = '';
      render();
    }, 3500);
  };

  const stopAuto = (A, msg) => {
    if (!A.on) return;
    A.on = false;
    if (msg) showNote(msg);
    render();
  };

  const runAuto = async (A) => {
    const target = Math.max(1, Math.min(G.acfg.target, stepsOf()));
    while (A.on && A.left > 0) {
      if (G.bet > G.bal) return stopAuto(A, 'Autoplay stopped: balance kam hai');

      const started = await doStart();
      if (!A.on) return; // user ne stop dabaya: round manual control me rehta hai
      if (started !== 'started') return stopAuto(A, started === 'resumed' ? 'Purana round resume hua, autoplay ruk gaya' : '');

      let out = 'safe';
      while (A.on && out === 'safe' && G.pos < target) {
        out = await doStep();
        if (out === 'safe') await wait(120);
      }
      if (!A.on) return;

      if (out === 'safe') {
        const ok = await doCash();
        if (!ok) return stopAuto(A, 'Cashout fail hua, autoplay ruk gaya');
      } else if (out !== 'dead' && out !== 'won') {
        return stopAuto(A, 'Autoplay stopped');
      }

      A.left -= 1;
      render();
      await wait(AUTO_GAP);
    }
    stopAuto(A);
  };

  const openAuto = () => {
    if (G.st !== 'idle' || G.busy || G.auto.on || !isActive) return;
    G.acfg.target = Math.max(1, Math.min(stepsOf(), G.acfg.target || 1));
    G.amod = true;
    render();
  };

  const startAuto = () => {
    if (G.st !== 'idle' || G.busy || G.auto.on) return;
    if (!(G.bet >= minBet) || G.bet > maxBet || G.bet > G.bal) {
      G.amod = false;
      flashErr();
      return;
    }
    const spins = Math.max(1, Math.min(100, Math.floor(G.acfg.spins) || 1));
    G.acfg.spins = spins;
    G.acfg.target = Math.max(1, Math.min(stepsOf(), G.acfg.target || 1));
    G.amod = false;
    const A = { on: true, left: spins };
    G.auto = A;
    render();
    runAuto(A);
  };

  const setSpins = (v) => {
    G.acfg.spins = Math.max(0, Math.min(100, Math.floor(Number(v)) || 0));
    render();
  };
  const setTarget = (v) => {
    G.acfg.target = Math.max(0, Math.min(stepsOf(), Math.floor(Number(v)) || 0));
    render();
  };

  playRef.current = play;

  const playBgm = () => {
    try {
      const p = bgmRef.current && bgmRef.current.play();
      if (p && p.catch) p.catch(() => {});
    } catch (e) {
      /* autoplay blocked */
    }
  };

  const setBet = (v) => {
    G.bet = v;
    G.betText = String(v);
    render();
  };

  const pickDifficulty = (k) => {
    if (G.st === 'playing') return;
    G.diff = k;
    board();
  };

  const toggleOpt = (k) => {
    G.opt[k] = !G.opt[k];
    if (k === 'mus' && G.started && bgmRef.current) {
      if (G.opt.mus) playBgm();
      else bgmRef.current.pause();
    }
    render();
  };

  const openModal = (m) => {
    G.menu = false;
    G.modal = m;
    render();
    if (m === 'hist') {
      G.histRows = null;
      G.histErr = '';
      G.histShown = HIST_PAGE;
      getChickenHistoryApi()
        .then((res) => {
          G.histRows = (res.data?.data || []).map(normalizeBet);
          render();
        })
        .catch((e) => {
          G.histErr = errMsg(e);
          G.histRows = [];
          render();
        });
    }
  };

  const goPage = (path) => {
    G.menu = false;
    navigate(path);
  };

  const closeModal = () => {
    G.modal = null;
    render();
  };

  // ---- effects ----
  useEffect(() => {
    if (bgmRef.current) bgmRef.current.volume = 0.5;
    const bgm = bgmRef.current;
    return () => {
      G.auto.on = false; // autoplay loop band
      clearTimeout(G.noteT);
      G.timers.forEach(clearTimeout);
      if (bgm) bgm.pause();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Space = Play / Go
  useEffect(() => {
    const onKey = (e) => {
      if (e.code === 'Space' && G.opt.spc && document.activeElement.tagName !== 'INPUT' && !G.modal) {
        e.preventDefault();
        playRef.current();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // menu ke bahar click = menu band
  useEffect(() => {
    const onClick = (e) => {
      if (G.menu && menuRef.current && !menuRef.current.contains(e.target)) {
        G.menu = false;
        render();
      }
    };
    document.addEventListener('click', onClick);
    return () => document.removeEventListener('click', onClick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Chicken aage badhe to scroller uske saath chale (user drag/scroll kar sakta hai)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const key = G.diff + ':' + G.pos;
    if (followKey.current === key) return;
    followKey.current = key;
    const cw = el.querySelector('#door')?.offsetWidth || 0;
    if (!cw) return;
    const visible = Math.floor(el.clientWidth / cw);
    const lead = Math.max(1, Math.min(4, visible - 2));
    el.scrollTo({ left: Math.max(0, G.pos - lead) * cw, behavior: 'smooth' });
  });

  // Hand tool: mouse se pakad ke drag karo (touch/trackpad native scroll se chalta hai)
  const onDragStart = (e) => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    const el = scrollRef.current;
    if (!el) return;
    dragRef.current = { x: e.clientX, left: el.scrollLeft };
    el.classList.add('grabbing');
    try {
      el.setPointerCapture(e.pointerId);
    } catch (err) {
      /* ignore */
    }
  };
  const onDragMove = (e) => {
    const d = dragRef.current;
    const el = scrollRef.current;
    if (!d || !el) return;
    el.scrollLeft = d.left - (e.clientX - d.x);
  };
  const onDragEnd = (e) => {
    const el = scrollRef.current;
    if (!dragRef.current || !el) return;
    dragRef.current = null;
    el.classList.remove('grabbing');
    try {
      el.releasePointerCapture(e.pointerId);
    } catch (err) {
      /* ignore */
    }
  };

  // Khelte waqt aage wali lanes ke grate se kabhi-kabhi chingaari (sirf decoration)
  useEffect(() => {
    const id = setInterval(() => {
      if (G.st !== 'playing' || G.busy) return;
      const total = difficulties[G.diff].multipliers.length;
      const pool = [];
      for (let i = G.pos; i < Math.min(total, G.pos + 7); i++) pool.push(i);
      if (!pool.length) return;
      const n = 1 + ((Math.random() * 2) | 0);
      setEmbers(Array.from({ length: n }, () => pool[(Math.random() * pool.length) | 0]), 700);
    }, 1400);
    return () => {
      clearInterval(id);
      clearTimeout(G.embT);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live wins ticker (sirf decoration, koi API nahi)
  useEffect(() => {
    const id = setInterval(() => {
      G.live = {
        online: 1200 + ((Math.random() * 150) | 0),
        name: LIVE_NAMES[(Math.random() * LIVE_NAMES.length) | 0],
        amt: '+₹' + (Math.random() * 400 + 5).toFixed(2),
      };
      render();
    }, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- derived values ----
  const playing = G.st === 'playing';
  const m = mults();
  const quick = [minBet, minBet * 5, minBet * 10, minBet * 50].filter((a) => a <= maxBet);
  const heroClass = [G.hop ? 'hop' : '', G.burn ? 'burn' : '', G.char ? 'char' : '', G.comeback ? 'comeback' : '']
    .join(' ')
    .trim();
  const username = user?.username || 'Player';
  const isWinPop = Boolean(G.pop && G.pop.win);

  const modalTitle = { rules: 'Game rules', how: 'How to play?', fair: 'Provably fair', hist: 'My bet history' }[G.modal];
  let modalBody = null;
  if (G.modal === 'rules' || G.modal === 'how') modalBody = RULES;
  else if (G.modal === 'fair') {
    modalBody = <p>Every lane is an independent random draw. Settings are for demonstration only.</p>;
  } else if (G.modal === 'hist') {
    if (G.histRows === null) modalBody = <p className="mmsg">Loading...</p>;
    else if (G.histErr) modalBody = <p className="mmsg">{G.histErr}</p>;
    else if (!G.histRows.length) modalBody = <p className="mmsg">No bets yet.</p>;
    else {
      modalBody = (
        <div className="hlist">
          <div className="hrow hhead">
            <span>Date</span>
            <span>Bet</span>
            <span>Mult.</span>
            <span>Win</span>
            <span />
          </div>
          {G.histRows.slice(0, G.histShown).map((h) => {
            const won = h.outcome === 'cashed';
            return (
              <div className="hrow" key={h.id}>
                <span className="hd">{dtShort(h.createdAt)}</span>
                <span className="hb2"><i className="hcoin">₹</i>{fmt(h.bet)}</span>
                <span className="hm"><b className={won ? 'won' : ''}>x{won ? fmt(h.multiplier) : 0}</b></span>
                <span className="hw">{won ? <><i className="hcoin">₹</i>{fmt(h.win)}</> : '–'}</span>
                <svg className="hshield" viewBox="0 0 24 24" aria-label="Provably fair">
                  <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6zM8.5 12l2.5 2.5 4.5-5" />
                </svg>
              </div>
            );
          })}
        </div>
      );
    }
  }

  return (
    <div className="chicken-road">
      <header>
        <div className="logo">CHICKEN R<span className="egg" />AD</div>
        <div className="hr">
          <button className="hb" id="how" onClick={() => openModal('how')}>
            <i>i</i>How to play?
          </button>
          <div className={'hb' + (G.flash ? ' err' : '')} id="bal">
            <span>{fmt(G.bal)}</span>
            <i className="coin">₹</i>
          </div>
          <button
            className="hb"
            id="fs"
            onClick={() =>
              document.fullscreenElement
                ? document.exitFullscreen()
                : document.documentElement.requestFullscreen?.()
            }
          >
            ⛶
          </button>
          <button
            id="mb"
            onClick={(e) => {
              e.stopPropagation();
              G.menu = !G.menu;
              render();
            }}
          >
            <span />
            <span />
            <span />
          </button>
        </div>

        <div id="menu" ref={menuRef} className={G.menu ? 'open' : ''}>
          <div className="mu">
            <span className="av">{AVATARS[G.avatar]}</span>
            <b>{username}</b>
            <small
              onClick={() => {
                G.avatar = (G.avatar + 1) % AVATARS.length;
                render();
              }}
            >
              Change avatar
            </small>
          </div>
          <div className="row" onClick={() => toggleOpt('snd')}>
            <Icon d="M4 9v6h4l5 4V5L8 9H4zM16.5 9a4 4 0 0 1 0 6M19 6.5a8 8 0 0 1 0 11" />
            <span>Sound</span>
            <span className={'tg' + (G.opt.snd ? ' on' : '')} />
          </div>
          <div className="row" onClick={() => toggleOpt('mus')}>
            <Icon d="M9 18V5l11-2v13M9 18a3 3 0 1 1-3-3 3 3 0 0 1 3 3zM20 16a3 3 0 1 1-3-3 3 3 0 0 1 3 3z" />
            <span>Music</span>
            <span className={'tg' + (G.opt.mus ? ' on' : '')} />
          </div>
          <div className="row" onClick={() => toggleOpt('spc')}>
            <Icon d="M3 9v6h18V9" />
            <span>«Space» to play &amp; go</span>
            <span className={'tg' + (G.opt.spc ? ' on' : '')} />
          </div>
          <div className="sep" />
          <div className="row" onClick={() => openModal('fair')}>
            <Icon d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6zM9 12l2 2 4-4" />
            <span>Provably fair settings</span>
          </div>
          <div className="row" onClick={() => openModal('rules')}>
            <Icon d="M6 3h9l4 4v14H6zM14 3v5h5M9 13h7M9 17h7" />
            <span>Game rules</span>
          </div>
          <div className="row" onClick={() => openModal('hist')}>
            <Icon d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 7v5l3 2" />
            <span>My bet history</span>
          </div>
          <div className="sep" />
          <div className="row" onClick={() => goPage('/profile')}>
            <Icon d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 21a8 8 0 0 1 16 0" />
            <span>My profile</span>
          </div>
          <div className="row" onClick={() => goPage('/my-bets')}>
            <Icon d="M5 4h14v16H5zM9 9h6M9 13h6M9 17h4" />
            <span>Bet history page</span>
          </div>
          <div className="row" onClick={() => goPage('/results')}>
            <Icon d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
            <span>Results</span>
          </div>
          <div
            className="row danger"
            onClick={() => {
              logout();
              navigate('/login', { replace: true });
            }}
          >
            <Icon d="M10 4H5v16h5M15 8l5 4-5 4M20 12H9" />
            <span>Logout</span>
          </div>
          <div className="sep" />
          <div className="pw">Powered by <b>iNOUT</b></div>
        </div>
      </header>

      <div id="live">
        <div>Live wins: <span className="dot" />Online: <span id="on">{G.live.online}</span></div>
        <div id="lw">
          <span className="a">🐔</span>
          <span id="ln">{G.live.name}</span>
          <em id="lm">{G.live.amt}</em>
        </div>
      </div>

      <div id="scene">
        <div
          id="scroll"
          ref={scrollRef}
          onPointerDown={onDragStart}
          onPointerMove={onDragMove}
          onPointerUp={onDragEnd}
          onPointerCancel={onDragEnd}
        >
        <div id="world">
          <div id="door" />
          <div id="cols" style={{ display: 'flex' }}>
            {m.map((x, i) => {
              const coined = i < G.pos - 1; // paar ho chuki lane -> gold coin
              const d = i - G.pos; // chicken se aage kitni door
              const dim = d >= 4 ? ' dim2' : d >= 2 ? ' dim1' : '';
              const dead = G.fireAt === i + 1;
              const last = i === m.length - 1; // aakhri lane = golden egg
              return (
                <div key={G.diff + i} className={'col' + (last ? ' final' : '') + (i === G.pos - 1 ? ' on' : '') + (dead ? ' dead' : '')}>
                  {coined ? (
                    <b className="mc coined">
                      <CoinSvg />
                    </b>
                  ) : last ? (
                    <div className="alcove">
                      <span className="beam" key={'b' + G.eggKey} />
                      <div className="eggdrop" key={'d' + G.eggKey}>
                        <span className="goldegg" />
                        <b className="elabel">{x.toFixed(2)}x</b>
                      </div>
                      <span className="pedestal" />
                    </div>
                  ) : (
                    <b className={'mc' + (i < G.pos ? ' pass' : '') + dim}>{x.toFixed(2)}x</b>
                  )}
                  <i className="gr" />
                  <s />
                  {G.embers.includes(i) && !dead && (
                    <div className="ember" key={'e' + G.embKey}>
                      <EmberSprite />
                    </div>
                  )}
                  {dead && (
                    <div className="fire">
                      <FireSprite />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div id="endwall" aria-hidden="true">
            <svg className="lamp" viewBox="0 0 60 120">
              <line x1="30" y1="0" x2="30" y2="46" stroke="#8d93b8" strokeWidth="4" strokeDasharray="7 3" />
              <circle cx="30" cy="78" r="26" fill="rgba(255,255,255,.06)" />
              <rect x="14" y="50" width="32" height="10" rx="4" fill="#5b6192" />
              <path d="M16 60h28l-14 24z" fill="#ffd23f" />
              <path d="M22 62h16l-8 14z" fill="#fff3a6" />
            </svg>
          </div>
          <div id="hen" className={heroClass} style={{ '--p': G.pos }}>
            <img key={G.hopKey} src={henImg} alt="hen" />
            <img className="hen-dead" src={deadImg} alt="" draggable={false} />
          </div>
        </div>
        </div>

        <div id="pop" className={(G.popShow ? 'show' : '') + (G.pop && !G.pop.win ? ' lose' : '') + (isWinPop ? ' win' : '')}>
          {isWinPop && G.popShow && (
            <div className="confetti" aria-hidden="true">
              {CONFETTI.map((c, i) => (
                <span
                  key={i}
                  style={{
                    left: c.left + '%',
                    background: c.color,
                    animationDelay: c.delay + 's',
                    animationDuration: c.dur + 's',
                    transform: 'rotate(' + c.rot + 'deg)',
                  }}
                />
              ))}
            </div>
          )}
          {isWinPop && <div className="pc">🎉 Congratulations! 🎉</div>}
          <div id="pt">{G.pop && !G.pop.win ? 'OOPS!' : 'WIN!'}</div>
          <div id="pm">{G.pop && !G.pop.win ? 'Burnt!' : 'x ' + (G.pop ? G.pop.mult.toFixed(2) : '0.00')}</div>
          <div id="pa">
            {G.pop ? (G.pop.win ? '+ ' : '- ') + fmt(G.pop.amt) : ''} <i className="coin">₹</i>
          </div>
        </div>

        {!isActive && <div id="cfg-off">Game is currently disabled</div>}
      </div>
      <div id="ground" />

      <div id="ctl">
        <div id="panel" className={playing || G.auto.on ? 'lock' : ''}>
          <div id="bets">
            <div id="bx">
              <button className="sm" id="mn" onClick={() => setBet(minBet)}>MIN</button>
              <input
                id="bv"
                inputMode="decimal"
                value={G.betText}
                onChange={(e) => {
                  G.betText = e.target.value;
                  G.bet = parseFloat(e.target.value.replace(',', '.')) || 0;
                  render();
                }}
              />
              <button className="sm" id="mx" onClick={() => setBet(Math.min(maxBet, Math.floor(G.bal)))}>MAX</button>
            </div>
            <div id="qb">
              {quick.map((a) => (
                <button key={a} onClick={() => setBet(a)}>
                  {a} <i className="coin">₹</i>
                </button>
              ))}
            </div>
          </div>

          <div id="mid">
            <div className="h">
              <p>Difficulty</p>
              <span>Chance of collision</span>
            </div>
            <div id="dt">
              {diffKeys.map((k) => (
                <button key={k} className={G.diff === k ? 'act' : ''} onClick={() => pickDifficulty(k)}>
                  {cap(k)}
                </button>
              ))}
            </div>
          </div>

          <button
            id="rb"
            className={(playing && !G.auto.on ? 'cash' : '') + (G.auto.on ? ' stop' : '')}
            aria-label={G.auto.on ? 'Stop autoplay' : playing ? 'Cash out' : 'Autoplay'}
            disabled={G.auto.on ? false : playing ? !G.pos || G.busy : G.busy || G.st !== 'idle' || !isActive}
            onClick={() => {
              if (G.auto.on) stopAuto(G.auto);
              else if (playing) cash();
              else openAuto();
            }}
          >
            {G.auto.on ? (
              <svg viewBox="0 0 24 24" width="30" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            ) : playing ? (
              <>
                CASH OUT<small>{G.pos ? fmt(G.bet * m[G.pos - 1]) : '0'} ₹</small>
              </>
            ) : (
              <svg viewBox="0 0 48 48" width="50" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M8 22a16 16 0 0 1 28-8M40 26a16 16 0 0 1-28 8M36 6v8h-8M12 42v-8h8" />
                <path d="M20 18l12 6-12 6z" fill="#fff" />
              </svg>
            )}
          </button>

          <button
            id="pb"
            className={G.auto.on ? 'auto' : ''}
            disabled={G.auto.on ? false : G.busy || (!isActive && !playing)}
            onClick={play}
          >
            {G.auto.on ? G.auto.left : playing ? 'GO' : 'Play'}
          </button>
        </div>
      </div>

      {G.note && <div id="note">{G.note}</div>}

      <audio ref={bgmRef} loop preload="auto" src={bgmSrc} />

      <div
        id="mod"
        className={G.modal ? 'show' : ''}
        onClick={(e) => {
          if (e.target === e.currentTarget) closeModal();
        }}
      >
        <div className={'mcard' + (G.modal === 'hist' ? ' wide' : '')}>
          <div className="mhead">
            <h3>{modalTitle}</h3>
            <button className="mx" aria-label="Close" onClick={closeModal}>
              <svg viewBox="0 0 24 24" width="20" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
          <div className="mbody">{modalBody}</div>
          {G.modal === 'hist' && G.histRows && G.histRows.length > G.histShown && (
            <button
              className="mbtn"
              onClick={() => {
                G.histShown += HIST_PAGE;
                render();
              }}
            >
              LOAD MORE
            </button>
          )}
          {G.modal === 'hist' && (
            <button
              className="mlink"
              onClick={() => {
                G.modal = null;
                navigate('/my-bets');
              }}
            >
              Full history
            </button>
          )}
        </div>
      </div>

      {/* ---------- AUTOPLAY MODAL ---------- */}
      <div
        id="amod"
        className={G.amod ? 'show' : ''}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            G.amod = false;
            render();
          }
        }}
      >
        <div className="mcard acard">
          <div className="mhead">
            <h3>Autoplay</h3>
            <button
              className="mx"
              aria-label="Close"
              onClick={() => {
                G.amod = false;
                render();
              }}
            >
              <svg viewBox="0 0 24 24" width="20" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
          <div className="mbody">
            <label className="alabel">Numbers of autospins:</label>
            <div className="astep">
              <button aria-label="Less" onClick={() => setSpins(G.acfg.spins - 1)}>−</button>
              <input
                inputMode="numeric"
                value={G.acfg.spins ? String(G.acfg.spins) : ''}
                onChange={(e) => setSpins(e.target.value.replace(/\D/g, ''))}
                onBlur={() => G.acfg.spins < 1 && setSpins(1)}
              />
              <button aria-label="More" onClick={() => setSpins(G.acfg.spins + 1)}>+</button>
            </div>
            <div className="apresets">
              {SPIN_PRESETS.map((n) => (
                <button key={n} className={G.acfg.spins === n ? 'act' : ''} onClick={() => setSpins(n)}>
                  {n}
                </button>
              ))}
            </div>

            <label className="alabel">Step to cashout?</label>
            <div className="astep">
              <button aria-label="Less" onClick={() => setTarget(G.acfg.target - 1)}>−</button>
              <input
                inputMode="numeric"
                value={G.acfg.target ? String(G.acfg.target) : ''}
                onChange={(e) => setTarget(e.target.value.replace(/\D/g, ''))}
                onBlur={() => G.acfg.target < 1 && setTarget(1)}
              />
              <button aria-label="More" onClick={() => setTarget(G.acfg.target + 1)}>+</button>
            </div>

            {(() => {
              const t = Math.max(1, Math.min(stepsOf(), G.acfg.target || 1));
              const start = Math.max(0, t - 2);
              const lanes = m.slice(start, start + 6);
              return (
                <>
                  <div className="aprev">
                    {start === 0 && <div className="adoor" />}
                    {lanes.map((x, k) => {
                      const idx = start + k;
                      const hit = idx === t - 1;
                      return (
                        <div className={'alane' + (hit ? ' hit' : '')} key={G.diff + idx}>
                          <b>{x.toFixed(2)}x</b>
                          <i />
                          {hit && <img src={henImg} alt="" />}
                        </div>
                      );
                    })}
                  </div>
                  <div className="astats">
                    <div>
                      <small>Coef</small>
                      <b>x{fmt(m[t - 1])}</b>
                    </div>
                    <div>
                      <small>Win</small>
                      <b>
                        <i className="hcoin">₹</i>
                        {fmt(G.bet * m[t - 1])}
                      </b>
                    </div>
                  </div>
                </>
              );
            })()}

            <button className="mbtn start" onClick={startAuto}>
              START ({Math.max(1, G.acfg.spins || 1)})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
